import { WorkoutLayerDomain } from '@strength/domain';
import {
  HasWorkoutLayer,
  WorkoutExercise,
  WorkoutSection,
} from '@strength/domain-workout';
import { I18nTranslator } from '@strength/i18n';
import {
  nextSortOrder,
  previousSortOrder,
  sortByOrder,
  sortOrdersWithinBounds,
} from '@strength/utils';
import { MdLink } from '@teamworksdev/icons';
import { useTranslation } from 'react-i18next';
import {
  ContextMenuItem,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
} from '../../../components/context-menu/context-menu';
import {
  WorkoutLayerCommand,
  WorkoutLayerModelName,
} from '../mutation-fns/types';
import {
  addSupersetEdits,
  addSupersetExerciseEdits,
  createWorkoutLayerEdit,
  findWorkoutLayer,
  mergeSupersetEdits,
  removeSupersetEdits,
  removeSupersetExerciseEdits,
} from '../mutation-fns/workout-draft';
import { ExerciseGroupedData } from '../types';
import { exercisesForSection } from '../utils';
import { ContextMenuItemIcon } from './context-menu-item-icon';
import { canRemoveExerciseFromSuperset } from './utils';

// An action that will be rendered as a ContextMenuItem, where "fn" will be run when the item is selected
type SupersetAction = {
  text: string;
  fn: (
    workoutLayer: HasWorkoutLayer | undefined,
    baseLayer: HasWorkoutLayer | undefined,
  ) => WorkoutLayerDomain.WorkoutLayerEdit[];
};

type SupersetActions = {
  removeSuperset: SupersetAction | null;
  removeExerciseFromSuperset: SupersetAction | null;
  // Add the entry above to a superset, or create a new superset
  addAbove: SupersetAction | null;
  // Add the entry below to a superset, or create a new superset
  addBelow: SupersetAction | null;
};

export function SupersetContextMenuItems({
  modelName,
  exerciseGroupedData,
  workoutLayers,
  bulkRunCommands,
  blockWorkoutLayers,
}: {
  modelName: WorkoutLayerModelName;
  exerciseGroupedData: ExerciseGroupedData;
  workoutLayers: HasWorkoutLayer[];
  bulkRunCommands: (commands: WorkoutLayerCommand[]) => void;
  blockWorkoutLayers?: HasWorkoutLayer[];
}) {
  const { t } = useTranslation();
  const supersetActions = getSupersetActions(exerciseGroupedData, t);
  if (!supersetActions) {
    return null;
  }

  function onSelect(actionKey: keyof SupersetActions) {
    const action = supersetActions?.[actionKey];
    if (!action) {
      return;
    }

    const { identity, workoutLayer, baseLayer } = findWorkoutLayer(
      workoutLayers,
      exerciseGroupedData.exercise.workoutId,
      modelName,
      modelName.toLowerCase().includes('athlete')
        ? blockWorkoutLayers
        : undefined,
    );

    const edits = action.fn(workoutLayer, baseLayer);
    bulkRunCommands([createWorkoutLayerEdit(identity, workoutLayer, edits)]);
  }

  const disabled =
    !supersetActions.addAbove &&
    !supersetActions.addBelow &&
    !supersetActions.removeSuperset &&
    !supersetActions.removeExerciseFromSuperset;

  return (
    <ContextMenuSub>
      <ContextMenuSubTrigger disabled={disabled}>
        <ContextMenuItemIcon IconPath={MdLink} />
        {t('superset')}
      </ContextMenuSubTrigger>
      <ContextMenuSubContent>
        {supersetActions.addAbove && (
          <ContextMenuItem onSelect={() => onSelect('addAbove')}>
            {supersetActions.addAbove.text}
          </ContextMenuItem>
        )}
        {supersetActions.addBelow && (
          <ContextMenuItem onSelect={() => onSelect('addBelow')}>
            {supersetActions.addBelow.text}
          </ContextMenuItem>
        )}
        {supersetActions.removeSuperset && (
          <ContextMenuItem onSelect={() => onSelect('removeSuperset')}>
            {supersetActions.removeSuperset.text}
          </ContextMenuItem>
        )}
        {supersetActions.removeExerciseFromSuperset && (
          <ContextMenuItem
            onSelect={() => onSelect('removeExerciseFromSuperset')}
          >
            {supersetActions.removeExerciseFromSuperset.text}
          </ContextMenuItem>
        )}
      </ContextMenuSubContent>
    </ContextMenuSub>
  );
}

export function getSupersetActions(
  exerciseGroupedData: ExerciseGroupedData,
  t: I18nTranslator,
): SupersetActions {
  const exercise = exerciseGroupedData.exercise;
  const section = exerciseGroupedData.section;
  // Show superset actions for all exercises, except for those that are in an alternate group.
  // Show only the superset actions for the base exercise in an alternate group.
  const exercises = exercisesForSection(section).filter(
    (e) =>
      !e.alternateGroup ||
      e.exercise.workoutExerciseId ===
        e.alternateGroup.exercises.find((ae) => ae.sortOrder === 0)?.exercise
          .workoutExerciseId,
  );
  const exerciseData = exercises.find((e) => e.exercise === exercise);
  if (exerciseData?.superset) {
    return getSupersetActionsForExerciseInSuperset(
      exercise,
      exercises,
      section,
      t,
    );
  } else {
    return getSupersetActionsForExercise(
      exercise,
      exercises,
      section.workoutSectionId,
      t,
    );
  }
}

export function getSupersetActionsForExercise(
  exercise: WorkoutExercise,
  exercises: ReturnType<typeof exercisesForSection>,
  workoutSectionId: string,
  t: I18nTranslator,
): SupersetActions {
  const index = exercises.findIndex(
    (e) => e.exercise.workoutExerciseId === exercise.workoutExerciseId,
  );
  let addAbove: SupersetAction | null = null;
  const aboveEntry = exercises[index - 1];
  if (aboveEntry) {
    const fn = () => {
      if (!aboveEntry.superset) {
        // Creates a new superset with the current exercise and the exercise above
        return addSupersetEdits(
          workoutSectionId,
          aboveEntry.exercise.sortOrder,
          [
            {
              sortOrder: 0,
              workoutExerciseId: aboveEntry.exercise.workoutExerciseId,
            },
            {
              sortOrder: 1,
              workoutExerciseId: exercise.workoutExerciseId,
            },
          ],
        );
      } else if (aboveEntry.superset) {
        // Adds the current exercise to the superset above
        return addSupersetExerciseEdits(aboveEntry.superset.workoutSupersetId, {
          sortOrder: nextSortOrder(aboveEntry.superset.exercises),
          workoutExerciseId: exercise.workoutExerciseId,
        });
      }

      return [];
    };
    addAbove = {
      fn,
      text: aboveEntry.superset
        ? t('addToSupersetAbove')
        : t('createWithExerciseAbove'),
    };
  }

  let addBelow: SupersetAction | null = null;
  const belowEntry = exercises[index + 1];
  if (belowEntry) {
    const fn = () => {
      if (!belowEntry.superset) {
        // Creates a new superset with the current exercise and the exercise below
        return addSupersetEdits(workoutSectionId, exercise.sortOrder, [
          {
            sortOrder: 0,
            workoutExerciseId: exercise.workoutExerciseId,
          },
          {
            sortOrder: 1,
            workoutExerciseId: belowEntry.exercise.workoutExerciseId,
          },
        ]);
      } else if (belowEntry.superset) {
        // Adds the current exercise to the superset below
        return addSupersetExerciseEdits(belowEntry.superset.workoutSupersetId, {
          sortOrder: previousSortOrder(belowEntry.superset.exercises),
          workoutExerciseId: exercise.workoutExerciseId,
        });
      }

      return [];
    };
    addBelow = {
      fn,
      text: belowEntry.superset
        ? t('addToSupersetBelow')
        : t('createWithExerciseBelow'),
    };
  }

  return {
    removeSuperset: null,
    removeExerciseFromSuperset: null,
    addAbove,
    addBelow,
  };
}

export function getSupersetActionsForExerciseInSuperset(
  exerciseInSuperset: WorkoutExercise,
  exercises: ReturnType<typeof exercisesForSection>,
  section: WorkoutSection,
  t: I18nTranslator,
): SupersetActions {
  const currentExerciseData = exercises.find(
    (e) =>
      e.exercise.workoutExerciseId === exerciseInSuperset.workoutExerciseId,
  );
  const superset = currentExerciseData?.superset;

  if (!superset) {
    return {
      removeSuperset: null,
      removeExerciseFromSuperset: null,
      addAbove: null,
      addBelow: null,
    };
  }

  const currentIndex = exercises.findIndex(
    (e) =>
      e.exercise.workoutExerciseId === exerciseInSuperset.workoutExerciseId,
  );

  let addAbove: SupersetAction | null = null;
  const aboveEntry = exercises[currentIndex - 1];
  if (aboveEntry) {
    const fn = (workoutLayer: HasWorkoutLayer | undefined) => {
      if (!aboveEntry.superset) {
        // Adds the exercise above to the current superset
        return addSupersetExerciseEdits(superset.workoutSupersetId, {
          sortOrder: previousSortOrder(superset.exercises),
          workoutExerciseId: aboveEntry.exercise.workoutExerciseId,
        });
      } else if (aboveEntry.superset) {
        const aboveSuperset = aboveEntry.superset;
        // Merges the superset above and the current superset
        const firstSortOrder = nextSortOrder(aboveSuperset.exercises) + 1;
        return mergeSupersetEdits(
          workoutLayer,
          superset.workoutSupersetId,
          aboveSuperset.workoutSupersetId,
          aboveSuperset.sortOrder ?? 0,
          superset.exercises.map(({ workoutExerciseId }, index) => ({
            workoutExerciseId,
            sortOrder: firstSortOrder + index,
          })),
        );
      }

      return [];
    };

    // If above exercise is apart of the same superset, no need to show a merge option
    if (
      aboveEntry.superset &&
      currentExerciseData.superset?.workoutSupersetId ===
        aboveEntry.superset.workoutSupersetId
    ) {
      addAbove = null;
    } else {
      addAbove = {
        fn,
        text: !aboveEntry.superset
          ? t('mergeWithExerciseAbove')
          : t('mergeWithSupersetAbove'),
      };
    }
  }

  let addBelow: SupersetAction | null = null;
  const belowEntry = exercises[currentIndex + 1];
  if (belowEntry) {
    const fn = (workoutLayer: HasWorkoutLayer | undefined) => {
      if (!belowEntry.superset) {
        // Adds the exercise below to the current superset
        return addSupersetExerciseEdits(superset.workoutSupersetId, {
          sortOrder: nextSortOrder(superset.exercises),
          workoutExerciseId: belowEntry.exercise.workoutExerciseId,
        });
      } else if (belowEntry.superset) {
        // Merges the superset below and the current superset
        const firstSortOrder =
          previousSortOrder(belowEntry.superset.exercises) - 1;
        return mergeSupersetEdits(
          workoutLayer,
          superset.workoutSupersetId,
          belowEntry.superset.workoutSupersetId,
          currentExerciseData.superset?.sortOrder ?? 0,
          superset.exercises.map(({ workoutExerciseId }, index) => ({
            workoutExerciseId,
            sortOrder: firstSortOrder + index,
          })),
        );
      }

      return [];
    };

    // If below exercise is apart of the same superset, no need to show a merge option
    if (
      belowEntry.superset &&
      currentExerciseData.superset?.workoutSupersetId ===
        belowEntry.superset.workoutSupersetId
    ) {
      addBelow = null;
    } else {
      addBelow = {
        fn,
        text: !belowEntry.superset
          ? t('mergeWithExerciseBelow')
          : t('mergeWithSupersetBelow'),
      };
    }
  }

  const removeSuperset: SupersetAction = {
    text: t('removeSuperset'),
    fn: (workoutLayer) => {
      const sortedEntries = sortByOrder(section.entries);
      const supersetIndex = sortedEntries.findIndex(
        (entry) =>
          entry.__type === 'WorkoutSuperset' &&
          entry.workoutSupersetId === superset.workoutSupersetId,
      );

      const nextSortOrder = sortedEntries[supersetIndex + 1]?.sortOrder;
      const newSortOrders = sortOrdersWithinBounds(
        [superset.sortOrder, nextSortOrder],
        superset.exercises.length,
      );
      const exerciseSortOrders = sortByOrder(superset.exercises).map(
        (se, index) => ({
          workoutExerciseId: se.workoutExerciseId,
          sortOrder: newSortOrders[index] ?? 0,
        }),
      );

      return removeSupersetEdits(
        workoutLayer,
        superset.workoutSupersetId,
        exerciseSortOrders,
      );
    },
  };

  let removeExerciseFromSuperset: SupersetAction | null = null;
  if (canRemoveExerciseFromSuperset(superset)) {
    removeExerciseFromSuperset = {
      text: t('removeExerciseFromSuperset'),
      fn: (workoutLayer, baseLayer) => {
        const supersetExercise = superset.exercises.find(
          (e) => e.workoutExerciseId === exerciseInSuperset.workoutExerciseId,
        );
        if (!supersetExercise) {
          return [];
        }
        return removeSupersetExerciseEdits(
          workoutLayer,
          baseLayer,
          superset.workoutSupersetId,
          supersetExercise.workoutSupersetExerciseId,
        );
      },
    };
  }

  return {
    removeSuperset,
    removeExerciseFromSuperset,
    addAbove,
    addBelow,
  };
}
